/**
 * The `/engagement` tab vocabulary — the three areas the page merges, plus the
 * mapping from the three retired surfaces that used to hold them.
 *
 * Pure and React-free on purpose. The redirect table, the page, the sidebar's
 * active-row match and the tests all need to agree on these strings, and a
 * literal repeated in four places is a literal that eventually disagrees with
 * itself. Everything here is exhaustively unit-tested in `./engagement-tabs.test.ts`.
 */

export const ENGAGEMENT_PATH = '/engagement';

/**
 * Ordered left to right, and the order is the argument for the merge: what the
 * assistant offers first, when it speaks first, and where it appears at all.
 * A visitor meets them in that sequence, so an operator reads them in it too.
 *
 * `rules` comes last because it is the only tab that is not one axis of that
 * sequence — it is the whole of it, authored on the engagement-rule engine and
 * measured. The three before it configure the surfaces a workspace has today;
 * this one is where an opener, a nudge or a campaign is written as a RULE, with
 * the funnel it produced beside it. Both are live at once on purpose: the v1
 * proactive config still serves visitors, so `rules/ProactiveImportCard` is the
 * bridge rather than a switch somebody has to be ready for. Appended rather
 * than inserted so no existing bookmark's tab moves.
 */
export const ENGAGEMENT_TABS = ['suggestions', 'nudges', 'placement', 'rules'] as const;
export type EngagementTab = (typeof ENGAGEMENT_TABS)[number];

/** The landing tab: suggestions are on for every tenant, so they are never empty. */
export const DEFAULT_ENGAGEMENT_TAB: EngagementTab = 'suggestions';

/** The query param carrying the tab, matching `/page-rules`' established `?tab=`. */
export const ENGAGEMENT_TAB_PARAM = 'tab';

export function isEngagementTab(value: string): value is EngagementTab {
  return (ENGAGEMENT_TABS as readonly string[]).includes(value);
}

/**
 * The tab a URL asks for. An unknown or absent value falls back to the landing
 * tab rather than rendering a blank surface — a stale bookmark from a rename
 * should still show the page.
 */
export function readEngagementTab(search: URLSearchParams): EngagementTab {
  const raw = search.get(ENGAGEMENT_TAB_PARAM) ?? '';
  return isEngagementTab(raw) ? raw : DEFAULT_ENGAGEMENT_TAB;
}

/** A linkable path for one tab — used by the redirects and by cross-links. */
export function engagementTabPath(tab: EngagementTab): string {
  return `${ENGAGEMENT_PATH}?${ENGAGEMENT_TAB_PARAM}=${tab}`;
}

/**
 * Where each retired surface's bookmarks land.
 *
 * `/page-rules` carried its OWN `?tab=all|specific`, and both halves live on the
 * placement tab now — the defaults as the state card at the top, the rules as
 * the exceptions list below it — so one destination serves both and the old
 * param is simply dropped.
 *
 * These redirects DO preserve the incoming search, and that is only safe
 * because the fold is destination-wins (`mergeRedirectSearch`): an incoming
 * `?tab=specific` is REPLACED by the `?tab=placement` named here rather than
 * fighting it, while `?nudge=<id>` — a real deep link the nudge drawer hands
 * people — still travels. This paragraph said the opposite until 2026-08-28,
 * describing a concatenating redirect that no longer exists; the route table
 * had carried `preserveSearch: true` on all three for some time.
 */
export const RETIRED_SURFACE_TABS: Readonly<Record<string, EngagementTab>> = {
  '/starters': 'suggestions',
  '/proactive': 'nudges',
  '/page-rules': 'placement',
};
